import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { Group } from '../../models/group/group.model';
import { Organization } from '../../models/organization/organization.model';
import { Team } from '../../models/team/team.model';
import { User, UserMarkup } from '../../models/user/user.model';

import { CostComparison, RateGroup } from '../../models/cost-comparison/cost-comparison.model';
import { FeeGroupings } from '../../models/fee-groupings/fee-groupings.model';
import { FeeGroupingsService } from '../../models/fee-groupings/fee-groupings.service';
import { Statement, StatementStructureTypeEnum } from '../../models/statement/statement.model';
import { StatementService } from '../../models/statement/statement.service';
import { ErrorDialogService } from '../error-dialog/error-dialog.service';
import { ProposalService } from '../../models/proposal/proposal.service';
import { ProcessorComparisonFee, ProcessorComparisonFeeDetail } from '../../models/comparison-fee-groupings/processor-comparison-fee-grouping.model';

interface TempStmtObj {
    id: string;
    friendlyName: string;
    fileName: string;
    structure: StatementStructureTypeEnum;
    feeGroupings?: FeeGroupings;
    procRates?: Array<RateGroup | any>;
}

@Component({
    selector: 'app-add-cost-comparison-dialog',
    templateUrl: './add-cost-comparison-dialog.component.html',
    styleUrls: ['./add-cost-comparison-dialog.component.scss'],
    standalone: false
})
export class AddCostComparisonDialogComponent {
    merchantId: string;
    ccCount: number;

    loading = true;
    completedStatements: Array<Statement> = [];

    sortColumn: string = 'createdAt';
    sortDirection: 'asc' | 'desc' = 'desc';

    selection: Statement[] = [];

    isSelectingStatements = true;
    previousSelection: any;
    selectedStatements: any[] = [];

    costComparisonObjects: Array<Partial<CostComparison>> = [];
    costComparisonEditForms: Array<UntypedFormGroup> = [];

    private subs: Array<Subscription> = [];

    constructor(
        public dialogRef: MatDialogRef<AddCostComparisonDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: UntypedFormBuilder,
        public errorDialog: ErrorDialogService,
        private statementService: StatementService,
        private feeGroupingsService: FeeGroupingsService,
        private proposalService: ProposalService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.merchantId = this.data.merchantId;
        this.ccCount = this.data.ccCount;
    
        this.statementService.getCompletedMerchantStatements(this.merchantId)
            .then((statements: Statement[]) => {
                this.completedStatements = statements;
                this.selection = [];
                this.loading = false;
                this.changeDetectorRef.detectChanges();
            }).catch(error => {
                console.error(error);
                this.loading = false;
                this.changeDetectorRef.detectChanges();
                this.errorDialog.open('Access Forbidden', 'You do not currently have access to this resource.').subscribe(result => {
                    console.error(result);
                    this.dialogRef.close();
                });
            });
    }

    formatStatementName(name: string): string {
        return name ? name.replace(/ /g, '_') : '';
    }

    formatPdfName(name: string): string {
        if (!name) return '';
        return `${name.replace(/ /g, '_')}.pdf`;
    }

    sortBy(column: string) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
    }

    sortedStatements(): Statement[] {
        return [...this.completedStatements].sort((a, b) => {
            const valA = a[this.sortColumn] ?? '';
            const valB = b[this.sortColumn] ?? '';
            const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
            return this.sortDirection === 'asc' ? cmp : -cmp;
        });
    }

    isSelected(row: Statement): boolean {
        return this.selection.some(s => s.id === row.id);
    }

    toggleSelection(row: Statement) {
        if (this.isSelected(row)) {
            this.selection = this.selection.filter(s => s.id !== row.id);
        } else {
            this.selection = [...this.selection, row];
        }
    }

    isAllSelected(): boolean {
        return this.completedStatements.length > 0 && this.selection.length === this.completedStatements.length;
    }

    masterToggle() {
        this.selection = this.isAllSelected() ? [] : [...this.completedStatements];
    }

    checkboxLabel(row?: Statement): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.isSelected(row) ? 'deselect' : 'select'} row`;
    }

    changeStatements() {
        this.previousSelection = this.selection;
        this.isSelectingStatements = true;
    }

    cancelChangeStatements() {
        this.selection = this.previousSelection;
        this.selectStatements();
    }

    selectStatements() {
        this.isSelectingStatements = false;
        this.selectedStatements = [];
        this.costComparisonObjects = [];
        this.selection.forEach((statement: Statement) => {

            const stmtObj: TempStmtObj = {
                id: statement.id,
                friendlyName: statement.friendlyName,
                fileName: statement.fileName,
                structure: statement.structure,
                feeGroupings: statement.feeGroupings
            };

            const rates: Array<{ description: string; transactionRate: number; transactionFee: number }> = [];
            stmtObj.feeGroupings?.processor?.fees?.forEach(pf => {
                pf.feeDetails?.forEach(pfd => {
                    rates.push(_.pick(pfd, ['description', 'transactionRate', 'transactionFee']));
                });
            });
            stmtObj['procRates'] = _.sortBy(rates, 'description');
            this.selectedStatements.push(stmtObj);
            this.determineCostComparisons();
        });
    }

    determineCostComparisons() {
        if (this.selectedStatements.length === this.selection.length) {
            this.selectedStatements.forEach((selectedStmtObj, index) => {
                const newCostComparison: Partial<CostComparison> = {
                    friendlyName: `Cost Comparison ${this.ccCount + index + 1}`,
                    statementData: [
                        {
                            statementId: selectedStmtObj.id,
                            statementName: selectedStmtObj.friendlyName
                        }
                    ],
                    originalStructure: selectedStmtObj.structure,
                    proposedStructure: selectedStmtObj.structure,
                    rateGroups: [...selectedStmtObj.procRates],
                    originalFeeGroupings: [selectedStmtObj.feeGroupings]
                };
                this.costComparisonObjects.push(newCostComparison);
            });
    
            this.doComparisonFeesAndForm();
        }
    }

    doComparisonFeesAndForm() {
        for (const costComparisonObj of this.costComparisonObjects) {
            costComparisonObj.comparisonFeeGroupings =
                this.feeGroupingsService.aggregateFeeGroupings(costComparisonObj.originalFeeGroupings || []);

            const ccForm = this.fb.group({
                _id: costComparisonObj.id,
                friendlyName: [costComparisonObj.friendlyName, [Validators.required]]
            });
            this.costComparisonEditForms.push(ccForm);
        }
    }

    isCreateDisabled() {
        return false;
    }

    createCCs() {
        this.dialogRef.close({
            costComparisonObjects: this.costComparisonObjects,
            costComparisonEditForms: this.costComparisonEditForms
        });
    }

    static applyCustomRates(result: any, org: Organization, group: Group, team: Team, user: User) {
        let userMarkup: UserMarkup;

        let proposedRate, proposedFee;
        if (group == null) {
            proposedRate = org ? org.buyRates?.ratePerTrans ?? 0.07 : 0.07;
            proposedFee = org ? org.buyRates?.costPerTrans ?? 10 : 10;
        } else {
            userMarkup = User.getMarkup(user, group, team);

            const userRate = userMarkup.ratePerTrans;
            const userFee = userMarkup.costPerTrans;
            proposedRate = org
                ? org.buyRates?.ratePerTrans ?? 0.07 + userRate
                : 0.07;
            proposedFee = org
                ? org.buyRates?.costPerTrans ?? 10 + userFee
                : 10;
        }

        result.costComparisonObjects.forEach((cco: CostComparison) => {
            cco.comparisonFeeGroupings?.processor?.fees?.forEach((procFee: ProcessorComparisonFee) => {
                let proposedAmountSum: number = 0;
                procFee.feeDetails?.forEach((detail: ProcessorComparisonFeeDetail) => {
                    const detailProposedAmount = detail.monthlyTransactions
                        * (proposedRate * detail.volume + proposedFee);
                    proposedAmountSum += detailProposedAmount;
                    detail.proposedRate = proposedRate;
                    detail.proposedFee = proposedFee;
                    detail.proposedAmount = detailProposedAmount;
                });
                procFee.proposedTotalAmount = proposedAmountSum;
            });
        });

        return result;
    }
}